import React, {
	forwardRef,
	useCallback,
	useEffect,
	useImperativeHandle,
	useRef,
	useState
} from "react";

import ClearIcon from "@mui/icons-material/Clear";
import SearchIcon from "@mui/icons-material/Search";

import styles from "./SearchBar.module.styl";

const SearchBar = forwardRef(function SearchBar(
	{
		cancelOnEscape = true,
		className = "",
		closeIcon = <ClearIcon />,
		disabled = false,
		onCancelSearch,
		onChange,
		onRequestSearch,
		placeholder = "Search",
		searchIcon = <SearchIcon />,
		style,
		value: valueProp = "",
		...inputProps
	},
	ref
) {
	const inputRef = useRef(null);
	const [value, setValue] = useState(valueProp || "");

	useEffect(() => {
		setValue(valueProp || "");
	}, [valueProp]);

	useImperativeHandle(ref, () => ({
		focus: () => inputRef.current?.focus(),
		blur: () => inputRef.current?.blur(),
		clear: () => clearSearch()
	}));

	const requestSearch = useCallback(() => {
		onRequestSearch?.(value.trim());
	}, [onRequestSearch, value]);

	const clearSearch = useCallback(() => {
		setValue("");
		onChange?.("");
		onCancelSearch?.();
	}, [onChange, onCancelSearch]);

	const handleChange = useCallback((event) => {
		const nextValue = event.target.value;
		setValue(nextValue);
		onChange?.(nextValue);
	}, [onChange]);

	const handleKeyDown = useCallback((event) => {
		if (event.key === "Enter") {
			requestSearch();
			return;
		}

		if (cancelOnEscape && event.key === "Escape") {
			clearSearch();
		}
	}, [cancelOnEscape, clearSearch, requestSearch]);

	const rootClassName = [
		styles.root,
		disabled ? styles.disabled : "",
		className
	].filter(Boolean).join(" ");

	return (
		<div className={rootClassName} style={style}>
			<input
				{...inputProps}
				ref={inputRef}
				className={styles.input}
				type="search"
				value={value}
				placeholder={placeholder}
				disabled={disabled}
				onChange={handleChange}
				onKeyDown={handleKeyDown}
			/>

			<button
				type="button"
				className={styles.iconButton}
				onClick={requestSearch}
				disabled={disabled}
				aria-label={placeholder}
			>
				{searchIcon}
			</button>

			{value && (
				<button
					type="button"
					className={styles.iconButton}
					onClick={clearSearch}
					disabled={disabled}
					aria-label="Clear search"
				>
					{closeIcon}
				</button>
			)}
		</div>
	);
});

export default SearchBar;